import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Contador de oferta de 2 horas por visitante, en ciclos.
 * - En la primera visita se guarda el vencimiento (ahora + 2 h) en localStorage.
 * - Al recargar o volver a entrar desde el mismo navegador se continúa desde el vencimiento guardado.
 * - Cuando llega a cero, arranca un nuevo ciclo de 2 horas automáticamente (no muestra "finalizó").
 *
 * IMPORTANTE: el checkout de ImpulTienda es independiente de este contador.
 */
const STORAGE_KEY = 'bsdr-offer-expires-at';
export const OFFER_DURATION_MS = 2 * 60 * 60 * 1000;
type OfferTimer = {
  /** Milisegundos restantes del ciclo actual */
  remaining: number;
  /** Con el reinicio automático, la oferta nunca queda vencida */
  expired: boolean;
  /** false hasta leer localStorage, para no mostrar un valor incorrecto al cargar */
  ready: boolean;
};
const OfferTimerContext = createContext<OfferTimer>({
  remaining: OFFER_DURATION_MS,
  expired: false,
  ready: false
});
export function OfferTimerProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setExpiresAt(readOrCreateExpiry());
  }, []);
  useEffect(() => {
    if (expiresAt === null) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= expiresAt) setExpiresAt(rollForward(expiresAt, t));
    }, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  const remaining = expiresAt === null ? OFFER_DURATION_MS : Math.max(0, expiresAt - now);
  const value: OfferTimer = {
    remaining,
    expired: false,
    ready: expiresAt !== null
  };
  return <OfferTimerContext.Provider value={value} data-id="element-380" data-mp="2je3qvtoad6py">{children}</OfferTimerContext.Provider>;
}
export function useOfferTimer() {
  return useContext(OfferTimerContext);
}

/** Avanza el vencimiento en ciclos de 2 h hasta que quede en el futuro, y lo guarda. */
function rollForward(expiry: number, now: number): number {
  const cyclesPassed = Math.floor((now - expiry) / OFFER_DURATION_MS) + 1;
  const next = expiry + cyclesPassed * OFFER_DURATION_MS;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // localStorage no disponible: el ciclo sigue solo en esta visita
  }
  return next;
}
function readOrCreateExpiry(): number {
  const now = Date.now();
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) {
      return stored > now ? stored : rollForward(stored, now);
    }
    const fresh = now + OFFER_DURATION_MS;
    window.localStorage.setItem(STORAGE_KEY, String(fresh));
    return fresh;
  } catch {
    return now + OFFER_DURATION_MS;
  }
}